import React from 'react'
import styles from '../BillingInvoices/BillingInvoices.module.css'

/*
 * Receipts, straight from Stripe.
 *
 * This list used to be built from the `subscriptionsss` mirror, which updates
 * the SAME row on every renewal — so a customer twelve months in saw one line,
 * not twelve, and the amount was whatever the last webhook happened to write.
 * The rows now come from stripe.invoices.list, where every charge is its own
 * immutable record, so the history is the history.
 *
 * Shape (see backend/src/controllers/billingController.js):
 *   { id, number, created, amountPaid, currency, status, description,
 *     hostedInvoiceUrl, invoicePdf, subscriptionId }
 */

// A refund or a $0 coupon invoice is still real history and still worth
// showing — but only a `paid` invoice is a receipt. Anything open / uncollectible
// is money we are still waiting on, and labelling it like a payment would tell
// the customer they are square when they are not.
const STATUS_LABELS = {
  open: 'Unpaid',
  uncollectible: 'Failed',
  void: 'Voided',
}

const formatDateTime = (iso) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { time: '', date: '' }
  return {
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    // "28 May 2025" — haath se jodte hain; en-GB locale naye browsers me
    // September ko "Sept" likhta hai.
    date: `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`,
  }
}

const formatAmount = (amount, currency) => {
  const value = Number(amount)
  if (!Number.isFinite(value)) return '—'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(value)
  } catch {
    // Unknown/!ISO currency code — Intl throws rather than degrading, so fall
    // back to plain text instead of blanking the whole row.
    return `${currency || ''} ${value.toFixed(2)}`.trim()
  }
}

const BillingInvoices = ({ invoices, loading = false, error = '' }) => {
  if (loading) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>Billing & Invoices</h3>
        <p className={styles.noData}>Loading your invoices…</p>
      </div>
    )
  }

  // Stripe unreachable. Say so plainly rather than rendering an empty table,
  // which reads as "you have never been charged" — the most alarming possible
  // way to fail on a billing screen.
  if (error) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>Billing & Invoices</h3>
        <p className={styles.noData}>{error}</p>
      </div>
    )
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>Billing & Invoices</h3>
        <p className={styles.noData}>No invoices found.</p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Billing & Invoices</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>S.no</th>
            <th>Invoice Date</th>
            <th>Amount</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice, index) => {
            const { time, date } = formatDateTime(invoice.created)
            // An unpaid invoice's amount_paid is 0, which would render "$0.00"
            // next to a date and look like a free month. Show what was asked
            // for, tagged with why it isn't settled.
            const isPaid = invoice.status === 'paid'
            const amount = formatAmount(
              isPaid ? invoice.amountPaid : invoice.amountDue,
              invoice.currency
            )
            const statusLabel = isPaid ? null : STATUS_LABELS[invoice.status] || invoice.status
            // Stripe's own PDF when it exists, otherwise the hosted receipt
            // page — a finalised invoice always has at least one.
            const link = invoice.invoicePdf || invoice.hostedInvoiceUrl

            return (
              <tr key={invoice.id}>
                <td>{String(index + 1).padStart(2, '0')}</td>
                <td>
                  <div className={styles.dateCell}>
                    <span className={styles.time}>{time}</span>
                    <br />
                    <span className={styles.date}>{date}</span>
                  </div>
                </td>
                <td>
                  <span className={styles.amount}>{amount}</span>
                  {statusLabel && <span className={styles.statusTag}>{statusLabel}</span>}
                </td>
                <td>
                  <div className={styles.actionIcon}>
                    {link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.actionIcon}
                        title={invoice.number ? `Invoice ${invoice.number}` : 'View invoice'}
                      >
                        <img src='/svg/download-invoice.svg' alt='download-invoice' />
                      </a>
                    ) : (
                      <span className={styles.noInvoiceLink}>—</span>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default BillingInvoices
